import Cookies from 'js-cookie'
import {inIframe} from "../../../util/viewport";

import './cookieBanner.css';
import {registerFrameMessageHandler} from "../iframe-communication/iframeCommunication";
import {updateCookieConsentList} from "./cookieConsent";

function checkCategoryIfComplete(category, scope) {
  const categorySubCheckboxes = Array.from(scope.querySelectorAll(".cookie-consent-cookie-checkbox[data-category='"+category+"']"));
  const categoryComplete = categorySubCheckboxes.reduce(function(acc, checkbox) {
    return acc && checkbox.checked;
  }, true);
  const categoryCheckbox = scope.querySelector(".cookie-consent-category-checkbox[data-category='"+category+"']");
  if(categoryCheckbox === null) {
    return;
  }
  categoryCheckbox.checked = categoryComplete;
}

function findCookieCheckbox(cookieCode, scope) {
  return Array.from(scope.querySelectorAll(".cookie-consent-cookie-checkbox"))
    .find(checkbox => checkbox.dataset.cookie === cookieCode) ?? null;
}

function clearCookieHighlights(scope) {
  Array.from(scope.querySelectorAll(".cookie-consent-cookie-highlight")).forEach(
    entry => entry.classList.remove("cookie-consent-cookie-highlight")
  );
}

/**
 * Opens the cookie category the given cookie belongs to, scrolls its entry
 * into view and highlights it, so an element directly next to blocked content
 * (data-cookie-highlight="<cookie code>") points the visitor at the checkbox
 * they have to enable.
 */
function highlightCookie(cookieCode, scope) {
  const checkbox = findCookieCheckbox(cookieCode, scope);
  if(checkbox === null) {
    return;
  }

  scope.querySelector("#cookie-consent-detail-"+checkbox.dataset.category)?.classList.remove("hidden");

  const entry = checkbox.closest("label") ?? checkbox.parentElement;
  if(entry === null) {
    return;
  }
  entry.classList.add("cookie-consent-cookie-highlight");
  revealEntry(entry);
}

let stopReveal = null;

/**
 * Scrolls the entry to the top of the cookie list and keeps it there while the
 * layout settles. The panel is rendered with the fallback font first, so on a
 * cold page load the web fonts arrive after the first scroll and reflow the
 * text above the entry, which pushes it back out of view.
 */
function revealEntry(entry) {
  const area = entry.closest(".cookie-consent-cookie-area");
  if(area === null) {
    entry.scrollIntoView({block: "nearest"});
    return;
  }

  if(typeof stopReveal === "function") {
    stopReveal();
  }

  let cancelled = false;
  const cancel = () => {
    cancelled = true;
  };
  // the visitor's own scrolling wins over the automatic one
  const interactionEvents = ["wheel", "touchstart", "pointerdown", "keydown"];
  interactionEvents.forEach(type => {
    area.addEventListener(type, cancel, {once: true, passive: true});
  });

  const align = () => {
    if(cancelled || !entry.isConnected || !entry.classList.contains("cookie-consent-cookie-highlight")) {
      return;
    }
    const delta = entry.getBoundingClientRect().top - area.getBoundingClientRect().top;
    if(Math.abs(delta) > 1) {
      area.scrollTop += delta;
    }
  };

  let observer = null;
  let timeout = null;
  const stop = () => {
    cancelled = true;
    clearTimeout(timeout);
    observer?.disconnect();
    interactionEvents.forEach(type => area.removeEventListener(type, cancel));
    if(stopReveal === stop) {
      stopReveal = null;
    }
  };
  stopReveal = stop;
  timeout = setTimeout(stop, 4000);

  align();
  requestAnimationFrame(() => {
    align();
    requestAnimationFrame(align);
  });

  if(typeof ResizeObserver === "undefined") {
    return;
  }
  // anything above the entry grows or shrinks when late web fonts reflow the
  // text, so watching the category blocks covers every shift of the entry
  observer = new ResizeObserver(align);
  Array.from(area.querySelectorAll(".cookie-consent-cookie")).forEach(block => observer.observe(block));
  observer.observe(entry);
}

function toggleCookieConsentSettingsHandler(e, scope) {
  const cookieCode = e?.currentTarget?.dataset?.cookieHighlight ?? null;
  const applyHighlight = () => {
    clearCookieHighlights(scope);
    if(cookieCode !== null) {
      highlightCookie(cookieCode, scope);
    }
  };

  if(!inIframe()) {
    scope.querySelector("#cookie-consent-settings")?.classList.toggle("hidden");
    applyHighlight();
  } else {
    window.top.postMessage(
      {'type': 'seltmann-cms-toggle-cookie-menu', 'cookieCode': cookieCode},
      window.origin
    );
  }

  if(typeof e !== "undefined") {
    e.preventDefault();
  }
}

function fetchCookieSettings(scope) {
  const settings = {};
  Array.from(scope.querySelectorAll(".cookie-consent-cookie-checkbox")).forEach(checkbox => {
    settings[checkbox.dataset.cookie] = checkbox.checked;
  });
  return settings;
}

function applyCookieSettings(settings, scope) {
  if(typeof settings === "undefined") {
    return;
  }
  Array.from(scope.querySelectorAll(".cookie-consent-cookie-checkbox")).forEach(checkbox => {
    checkbox.checked = settings[checkbox.dataset.cookie] === true;
    checkCategoryIfComplete(checkbox.dataset.category, scope);
  });
}

function updateSettings(settings) {
  updateCookieConsentList(settings);
  notifyServer(settings);
  for (const cookieCode in settings) {
    if(settings[cookieCode] === true) {
      dispatchAllowedCookieMessage(cookieCode);
    }
  }
}

function notifyServer(settings) {
  fetch('index.php?controller=index&function=recordCookieSettings&ajax=1', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ settings })
  })
  .catch(error => {
    console.error('[cookie-banner] Error notifying server:', error);
  });
}

function dispatchAllowedCookieMessage(cookieCode) {
  window.postMessage(
    {
      "type": 'seltmann-cms-allow-cookie',
      "cookieCode": cookieCode,
    },
    window.location.origin
  );
}

function setUpCookieBanner(scope) {
  // anything pointing at a specific cookie opens the settings for it, no matter
  // whether it is one of the cookie icons or a plain link inside blocked content
  const settingsTriggers = new Set([
    ...Array.from(scope.querySelectorAll(".cookie-consent-open-settings")),
    ...Array.from(scope.querySelectorAll("[data-cookie-highlight]"))
  ]);
  Array.from(settingsTriggers).forEach(
    button => button.addEventListener("click",
      e => toggleCookieConsentSettingsHandler(e, scope)
    )
  );

  Array.from(scope.querySelectorAll(".cookie-consent-mail-open-settings")).forEach(button => button.addEventListener('click', e => {
    scope.querySelector("#cookie-consent-settings-mail-"+button.dataset.id)?.classList.toggle("hidden");
    e.preventDefault();
  }));

  Array.from(scope.querySelectorAll(".cookie-consent-close-settings")).forEach(button => button.addEventListener('click', e => {
    scope.querySelector("#cookie-consent-settings")?.classList.add("hidden");
    e.preventDefault();
  }));

  Array.from(scope.querySelectorAll(".cookie-consent-mail-close-settings")).forEach(button => button.addEventListener('click', e => {
    scope.querySelector("#cookie-consent-settings-mail-"+button.dataset.id)?.classList.add("hidden");
    e.preventDefault();
  }));

  scope.querySelector("#cookie-consent-reject")?.addEventListener('click', function(e) {
    Array.from(scope.querySelectorAll(".cookie-consent-cookie-checkbox:not(:disabled)")).forEach(function(checkbox) {
      checkbox.checked = false;
      checkCategoryIfComplete(checkbox.dataset.category, scope);
    });
    const settings = fetchCookieSettings(scope);
    Cookies.set('cms-cookie-consent', JSON.stringify(settings), { 'expires': 365 });
    updateSettings(settings);
    scope.querySelector(".cookie-consent-banner")?.classList.add('hidden');
    e.preventDefault();
  });

  scope.querySelector("#cookie-consent-accept")?.addEventListener('click', e => {
    scope.querySelectorAll(".cookie-consent-cookie-checkbox:not(:disabled)").forEach(checkbox => {
      checkbox.checked = true;
      checkCategoryIfComplete(checkbox.dataset.category, scope);
    });
    const settings = fetchCookieSettings(scope);
    Cookies.set('cms-cookie-consent', JSON.stringify(settings), { 'expires': 365 });
    updateSettings(settings);
    Array.from(scope.querySelectorAll(".cookie-consent-banner")).forEach(banner => banner.classList.add('hidden'));
    e.preventDefault();
  });

  Array.from(scope.querySelectorAll(".cookie-consent-details-link, .cookie-consent-close-button")).forEach(
    button => button.addEventListener('click', (e) => {
      scope.querySelector("#cookie-consent-detail-"+button.dataset.category)?.classList.toggle('hidden');
    })
  );

  Array.from(scope.querySelectorAll(".cookie-consent-category-checkbox")).forEach(checkbox => checkbox.addEventListener('change', e => {
    const checked = checkbox.checked;
    const category = checkbox.dataset.category;
    Array.from(scope.querySelectorAll(".cookie-consent-cookie-checkbox[data-category='"+category+"']")).forEach(
      checkbox => checkbox.checked = checked
    );
  }));

  Array.from(scope.querySelectorAll(".cookie-consent-cookie-checkbox")).forEach(
    checkbox => checkbox.addEventListener('change', () => checkCategoryIfComplete(checkbox.dataset.category, scope))
  );

  Array.from(scope.querySelectorAll(".cookie-consent-settings-button")).forEach(button => button.addEventListener('click', () => {
    const settings = fetchCookieSettings(scope);
    Cookies.set('cms-cookie-consent', JSON.stringify(settings), { 'expires': 365 });
    updateSettings(settings);
    Array.from(scope.querySelectorAll('.cookie-consent-banner')).forEach(banner => banner.classList.add('hidden'));
    Array.from(scope.querySelectorAll('.cookie-consent-settings')).forEach(settings => settings.classList.add('hidden'));
  }));

  // toggle cookie menu if asked by an iframe
  registerFrameMessageHandler('seltmann-cms-toggle-cookie-menu', message => {
    const settings = scope.querySelector("#cookie-consent-settings");
    settings?.classList.toggle("hidden");
    if(settings === null || settings.classList.contains("hidden")) {
      return;
    }
    clearCookieHighlights(scope);
    const cookieCode = message?.cookieCode ?? null;
    if(cookieCode !== null) {
      highlightCookie(cookieCode, scope);
    }
  });

  // show cookie banner if cookie is not present or invalid
  const cookieSettingsRaw = Cookies.get('cms-cookie-consent');
  let cookieSettings;
  try {
    cookieSettings = JSON.parse(cookieSettingsRaw);
  } catch (e) {
    console.debug('[cookie-banner] could not read cookie settings:', {error: e, cookieSettingsRaw});
    cookieSettings = undefined;
  }
  if(typeof cookieSettings !== 'object' && !inIframe()) {
    Array.from(scope.querySelectorAll(".cookie-consent-banner")).forEach(banner => banner.classList.remove('hidden'));
  }
  applyCookieSettings(cookieSettings, scope);
}

export {setUpCookieBanner};
